import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientServices, ClientService } from '../../client-services/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface ActiveServicesProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const ActiveServices: React.FC<ActiveServicesProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { services, isLoading } = useClientServices();

  const items = React.useMemo(() => {
    const arr = Array.isArray(services) ? services : [];
    return arr
      .filter((s: ClientService) => s.serviceActive)
      .sort((a: ClientService, b: ClientService) => {
        const da = new Date(a.serviceStartDate || 0).getTime();
        const db = new Date(b.serviceStartDate || 0).getTime();
        return db - da;
      })
      .slice(0, effectiveLimit);
  }, [services, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <GenericListWidget<ClientService>
      title="Active Services"
      viewAllPath="/client-services"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No active services"
      getItemKey={(s) => s.clientServicetId}
      embedded={embedded}
      renderItem={(service) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() =>
            navigate(`/client-services/${service.clientServicetId}`)
          }
          onKeyDown={(e) =>
            e.key === 'Enter' &&
            navigate(`/client-services/${service.clientServicetId}`)
          }
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text}`}>
              {service.client?.clientName || `Client #${service.clientId}`}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {service.serviceCategoryDescr?.psc_descr ||
                service.serviceTypeDescr?.pst_descr ||
                'Service'}
              {service.serviceEndDate &&
                ` · Ends ${new Date(service.serviceEndDate).toLocaleDateString('en-US')}`}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default ActiveServices;
